import React from 'react';
import { css } from '@emotion/css';
import ModalLayout from '../../../../components/structure/ModalLayout';
import ModalTitle from '../../../../components/general/ModalTitle';
import Button from '../../../../components/general/Button';
import HorizontalContainer from '../../../../components/structure/HorizontalContainer';
import CustomScrollbars from '../../../../components/structure/CustomScrollbars';
import { color_shades_dark, color_shades_lighter, dark_grey_blue_70 } from '../../../../constants/colors';
import FooterBar from '../../../../components/structure/FooterBar';

const closeIcon = require('../../../../content/images/close_icon.svg');

interface SalesforceInstructionsModalProps {
    onClose: () => void;
}

const SalesforceInstructionsModal: React.FC<SalesforceInstructionsModalProps> = ({ onClose }) => {
    return (
        <ModalLayout isVisible={true} onClose={onClose}>
            <div className={containerStyle}>
                <HorizontalContainer className={modalTitleContainer}>
                    <ModalTitle>Salesforce Integration Setup Guide</ModalTitle>
                    <Button themes={['end-tight', 'icon']} icon={closeIcon} onClick={onClose} />
                </HorizontalContainer>
                <CustomScrollbars autoHide>
                    <div className={contentStyle}>
                        <section>
                            <h3>Prerequisites</h3>
                            <p>Before you begin, make sure you have:</p>
                            <ul>
                                <li>A Salesforce account with administrator access</li>
                                <li>Access to Salesforce Setup</li>
                            </ul>
                        </section>

                        <section>
                            <h3>Step 1: Access Salesforce Setup</h3>
                            <ol>
                                <li>Log in to your Salesforce account</li>
                                <li>Click on the settings icon (gear) in the top right</li>
                                <li>Select "Setup" from the dropdown menu</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 2: Create a New External Client App</h3>
                            <ol>
                                <li>In the left sidebar, expand "Apps"</li>
                                <li>Expand "External Client Apps"</li>
                                <li>Click on "External Client App Manager"</li>
                                <li>Click "New External Client App" in the top right</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 3: Configure the App</h3>
                            <ol>
                                <li>Enter the following information:
                                    <ul>
                                        <li>App Name: Voicify</li>
                                        <li>API Name: Voicify</li>
                                        <li>Email: Support@voicify.com</li>
                                        <li>Distribution State: Packaged</li>
                                    </ul>
                                </li>
                                <li>Expand "API (Enable OAuth Settings)" and configure:
                                    <ul>
                                        <li>Check "Enable OAuth"</li>
                                        <li>Callback URL: https://spark.voicify.com/v/orgSettings</li>
                                        <li>Select OAuth Scopes:
                                            <ul>
                                                <li>Manage user data via APIs (api)</li>
                                                <li>Full access (full)</li>
                                                <li>Perform requests at any time (refresh_token, offline_access)</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 4: Configure Flow Settings</h3>
                            <ol>
                                <li>Enable the following flows:
                                    <ul>
                                        <li>Client Credentials Flow</li>
                                        <li>Authorization Code and Credentials Flow (keep Require user credentials in the POST body for Authorization Code and Credentials Flow unchecked)</li>
                                        <li>Device Flow</li>
                                        <li>Token Exchange Flow (keep Require secret for Token Exchange Flow unchecked)</li>
                                    </ul>
                                </li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 5: Configure Security Settings</h3>
                            <ol>
                                <li>Before creating the app, scroll down to the "Security" section</li>
                                <li>Turn off ALL of the following security options by unchecking them:
                                    <ul>
                                        <li>Require secret for Web Server Flow</li>
                                        <li>Require secret for Refresh Token Flow</li>
                                        <li>Require Proof Key for Code Exchange (PKCE) extension for Supported Authorization Flows</li>
                                        <li>Enable Refresh Token Rotation</li>
                                        <li>Issue JSON Web Token (JWT)-based access tokens for named users</li>
                                    </ul>
                                </li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 6: Create the App</h3>
                            <ol>
                                <li>Review all settings to ensure they are correct</li>
                                <li>Click "Create" to save your app</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 7: Configure OAuth Policies</h3>
                            <ol>
                                <li>After the app is created, click on the "Policies" tab</li>
                                <li>Click "Edit"</li>
                                <li>Expand "OAuth Policies"</li>
                                <li>Under "OAuth Flows and External Client App Enhancements":
                                    <ul>
                                        <li>Check ONLY "Enable Token Exchange Flow"</li>
                                        <li>Make sure all other flow options are unchecked</li>
                                    </ul>
                                </li>
                                <li>Under "App Authorization":
                                    <ul>
                                        <li>Check "Refresh token is valid until revoked"</li>
                                        <li>Set "IP Relaxation" to "Relaxed IP restrictions"</li>
                                    </ul>
                                </li>
                                <li>Click "Save" to apply the policy changes</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 8: Get Credentials</h3>
                            <ol>
                                <li>Click on the "Settings" tab</li>
                                <li>Expand "OAuth Settings"</li>
                                <li>Click on "Consumer Key and Secret"</li>
                                <li>Copy the following credentials:
                                    <ul>
                                        <li>Consumer Key (this will be your Client ID)</li>
                                        <li>Consumer Secret (this will be your Client Secret)</li>
                                    </ul>
                                </li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 9: Complete Integration</h3>
                            <ol>
                                <li>Return to Spark</li>
                                <li>Paste the Consumer Key into the "Client ID" field</li>
                                <li>Paste the Consumer Secret into the "Client Secret" field</li>
                                <li>Select your location from the dropdown menu</li>
                                <li>You may need to wait for 5-10 minutes for Salesforce to fully activate the app</li>
                                <li>Click "Authenticate" to complete the integration</li>
                            </ol>
                        </section>
                    </div>
                </CustomScrollbars>
                <FooterBar>
                    <Button themes={["primary", "wide"]} text="Close" onClick={onClose} />
                </FooterBar>
            </div>
        </ModalLayout>
    );
};

const containerStyle = css`
    display: flex;
    flex-direction: column;
    height: 90vh;
    width: 100%;
`;

const modalTitleContainer = css`
    padding: 24px;
    border-bottom: 1px solid ${color_shades_dark};
`;

const contentStyle = css`
    padding: 24px;
    padding-bottom: 120px;
    section {
        margin-bottom: 32px;
        
        &:last-child {
            margin-bottom: 0;
        }
        
        h3 {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 16px;
            color: #000;
        }
        
        p {
            font-size: 14px;
            line-height: 1.5;
            color: ${dark_grey_blue_70};
            margin-bottom: 12px;
        }
        
        ul, ol {
            margin-left: 24px;
            margin-bottom: 16px;
            
            li {
                font-size: 14px;
                line-height: 1.2;
                color: ${dark_grey_blue_70};
                margin-bottom: 8px;
                
                ul, ol {
                    margin-top: 8px;
                }
            }
        }
    }
`;

export default SalesforceInstructionsModal; 